import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../lib/analytics';
import { Layout } from '../components/layout/Layout';
import {
  UrlInputForm,
  TextInputForm,
  TranscriptModal,
  ReportConversionModal,
} from '../components/conversion';
import { AudioPlayer } from '../components/library';
import EditionCard, { conversionToEdition } from '../components/feed/EditionCard';
import { CreditBalance } from '../components/credits';
import { useConversions } from '../hooks';
import { Conversion } from '../types';
import { conversionsApi, downloadConversion, shareConversion } from '../api/conversions';
import { Spinner, OnboardingModal, SectionHeader, StatCard } from '../components/common';

const ONBOARDING_KEY = (userId: string) => `onboarding_seen_${userId}`;

export function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const {
    conversions,
    loading,
    error,
    total,
    createConversion,
    createTextConversion,
    deleteConversion,
    refreshConversion,
  } = useConversions({ limit: 5 });

  const [playingConversion, setPlayingConversion] = useState<Conversion | null>(null);
  const [playingAudioUrl, setPlayingAudioUrl] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [transcriptFor, setTranscriptFor] = useState<Conversion | null>(null);
  const [reportFor, setReportFor] = useState<Conversion | null>(null);
  const playRequestRef = useRef(0);
  const urlFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem(ONBOARDING_KEY(user.id));
    if (!seen) {
      setShowOnboarding(true);
    }
  }, [user?.id]);

  const handleOnboardingComplete = () => {
    if (user) {
      localStorage.setItem(ONBOARDING_KEY(user.id), '1');
    }
    setShowOnboarding(false);
    setTimeout(() => {
      urlFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleCreateConversion = async (url: string, title?: string) => {
    setCreateError(null);
    try {
      await createConversion(url, title);
      trackEvent('conversion_submitted', { input: 'url' });
      // The API has spent a credit — pick up the new balance.
      void refreshUser();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create conversion';
      setCreateError(message);
      throw err;
    }
  };

  const handleCreateTextConversion = async (content: string, title?: string) => {
    setCreateError(null);
    try {
      await createTextConversion(content, title);
      trackEvent('conversion_submitted', { input: 'paste' });
      void refreshUser();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create conversion';
      setCreateError(message);
      throw err;
    }
  };

  const handlePlay = async (conversion: Conversion) => {
    if (conversion.audioUrl) {
      trackEvent('audio_play', { surface: 'dashboard' });
      const requestId = ++playRequestRef.current;
      setPlaybackError(null);
      try {
        const { audioUrl } = await conversionsApi.getAudioUrl(conversion.id);
        if (requestId !== playRequestRef.current) return;
        setPlayingAudioUrl(audioUrl);
        setPlayingConversion(conversion);
      } catch (err) {
        if (requestId !== playRequestRef.current) return;
        setPlayingConversion(null);
        setPlayingAudioUrl(null);
        setPlaybackError(
          `${conversion.title || 'This conversion'}: ${err instanceof Error ? err.message : 'Failed to load audio'}`
        );
      }
    }
  };

  const isPlayerVisible = !!(playingConversion && playingAudioUrl);

  return (
    <Layout>
      {showOnboarding && <OnboardingModal onComplete={handleOnboardingComplete} rssFeedToken={user?.rssFeedToken} onConvert={handleCreateConversion} />}
      <div className={`max-w-4xl mx-auto animate-fade-in ${isPlayerVisible ? 'pb-32' : ''}`}>
        <SectionHeader
          eyebrow="dashboard"
          title={`Welcome back, ${user?.displayName?.split(' ')[0] || 'there'}`}
          description="Your text, turned into audio."
          className="mb-12"
        />

        {/* Stats Cards */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 animate-fade-in-up"
          style={{ animationDelay: '0.1s' }}
        >
          <StatCard label="Credit Balance" linkTo="/credits" linkText="buy more credits">
            <CreditBalance balance={user?.creditBalance || 0} size="lg" />
          </StatCard>

          <StatCard label="Conversions" linkTo="/collection" linkText="view collection">
            <div className="flex items-baseline gap-3">
              <span
                className="font-mono text-6xl font-normal tracking-[0.04em] leading-none tabular-nums"
                style={{
                  color: 'var(--color-led-green)',
                  textShadow: '0 0 12px rgba(58, 209, 122, 0.45)',
                }}
              >
                {total}
              </span>
              <span className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                {total === 1 ? 'conversion' : 'conversions'}
              </span>
            </div>
          </StatCard>

          <StatCard label="Podcast Feed" linkTo="/feeds" linkText="get feed URL">
            <p className="font-body text-sm text-muted-foreground">
              Your personal podcast feed
            </p>
          </StatCard>
        </div>

        {/* URL Input Form */}
        <div ref={urlFormRef} className="mb-12 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
          <SectionHeader
            eyebrow="add source"
            title="Add a source"
            description="Paste any URL. noctua reads it aloud and turns it into audio."
            level="h2"
            size="md"
            className="mb-6"
          />
          {createError && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
              <p className="font-body text-sm text-destructive">{createError}</p>
            </div>
          )}
          <UrlInputForm
            onSubmit={handleCreateConversion}
            creditCost={1}
            disabled={(user?.creditBalance ?? 0) < 1}
          />
          {(user?.creditBalance ?? 0) < 1 && (
            <p className="mt-3 font-body text-sm text-muted-foreground">
              You need credits to convert text to audio.{' '}
              <Link to="/credits" className="text-primary hover:opacity-80 font-medium underline transition-opacity">
                Buy credits
              </Link>
            </p>
          )}
        </div>

        {/* Paste-text Input Form */}
        <div className="mb-12 animate-fade-in-up" style={{ animationDelay: '0.225s' }}>
          <SectionHeader
            eyebrow="paste text"
            title="Paste your text here"
            description="Got a draft, transcript, or excerpt? Paste it in — noctua will read it just like a URL."
            level="h2"
            size="md"
            className="mb-6"
          />
          <TextInputForm
            onSubmit={handleCreateTextConversion}
            creditCost={1}
            disabled={(user?.creditBalance ?? 0) < 1}
          />
        </div>

        {/* Email to Podcast */}
        <div className="mb-12 animate-fade-in-up" style={{ animationDelay: "0.25s" }}>
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-lg font-medium text-foreground mb-1">
                  Forward newsletters by email
                </h2>
                <p className="font-body text-sm text-muted-foreground mb-4">
                  Forward any newsletter to noctua. It will be converted to audio and added to your feed automatically.
                </p>
                <div className="bg-muted/50 border border-border rounded-sm px-4 py-3 mb-4 flex items-center gap-3">
                  <svg className="w-4 h-4 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="font-mono text-sm text-foreground select-all">carrier-pigeon@noctua.uno</span>
                </div>
                <p className="font-body text-xs text-muted-foreground">
                  <span className="text-foreground font-medium">Important:</span> you must send from{' '}
                  <span className="font-mono">{user?.email}</span> — emails from other addresses are rejected.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Conversions */}
        <div className="animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <div className="flex items-end justify-between mb-6 pb-4 border-b border-border gap-4">
            <SectionHeader
              eyebrow={`recent · ${conversions.length} of ${total}`}
              title="Recent conversions"
              level="h2"
              size="md"
              className="mb-0"
            />
            {conversions.length > 0 && (
              <Link
                to="/collection"
                className="shrink-0 mb-1 font-mono text-xs tracking-[0.12em] lowercase text-primary hover:opacity-80 transition-opacity"
              >
                view all →
              </Link>
            )}
          </div>

          {playbackError && (
            <div
              role="alert"
              className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-sm flex items-start justify-between gap-4"
            >
              <p className="font-body text-sm text-destructive">{playbackError}</p>
              <button
                type="button"
                onClick={() => setPlaybackError(null)}
                className="font-mono text-xs tracking-widest uppercase text-muted-foreground hover:text-foreground"
              >
                Dismiss
              </button>
            </div>
          )}

          {loading && conversions.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="p-6 bg-destructive/10 border border-destructive/20 rounded-sm">
              <p className="font-body text-destructive">{error}</p>
            </div>
          ) : conversions.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border rounded-sm">
              <svg
                className="mx-auto h-12 w-12 text-muted-foreground opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                />
              </svg>
              <h3 className="mt-6 font-display text-xl font-medium text-foreground">
                No conversions yet
              </h3>
              <p className="mt-2 font-body text-muted-foreground">
                Paste a URL above to make your first conversion.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {conversions.map((conversion, idx) => (
                <EditionCard
                  key={conversion.id}
                  edition={conversionToEdition(conversion, conversions.length - idx)}
                  isActive={playingConversion?.id === conversion.id}
                  onPlay={(id) => {
                    const c = conversions.find((x) => x.id === id);
                    if (c) handlePlay(c);
                  }}
                  onDelete={(id) => {
                    if (window.confirm('Delete this conversion? This cannot be undone.')) {
                      deleteConversion(id).catch((err) =>
                        console.error('Failed to delete conversion:', err)
                      );
                    }
                  }}
                  onViewTranscript={(id) => {
                    const c = conversions.find((x) => x.id === id);
                    if (c) setTranscriptFor(c);
                  }}
                  onShare={(id) => {
                    const c = conversions.find((x) => x.id === id);
                    return shareConversion(id, c?.title);
                  }}
                  onDownload={() => downloadConversion(conversion.id, conversion.title)}
                  onReport={(id) => {
                    const c = conversions.find((x) => x.id === id);
                    if (c) setReportFor(c);
                  }}
                  onRenew={async (id) => {
                    setPlaybackError(null);
                    try {
                      await conversionsApi.renewConversion(id);
                      await refreshConversion(id);
                    } catch (err) {
                      setPlaybackError(err instanceof Error ? err.message : 'Failed to renew conversion');
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <TranscriptModal
        isOpen={transcriptFor !== null}
        onClose={() => setTranscriptFor(null)}
        title={transcriptFor ? `Transcript: ${transcriptFor.title}` : ''}
        transcript={transcriptFor?.transcript ?? null}
      />

      <ReportConversionModal
        isOpen={reportFor !== null}
        conversionId={reportFor?.id ?? null}
        conversionTitle={reportFor?.title}
        onClose={() => setReportFor(null)}
        onReported={(report) => void refreshConversion(report.conversionId)}
      />

      {/* Docked Audio Player */}
      {isPlayerVisible && (
        <AudioPlayer
          key={playingAudioUrl}
          src={playingAudioUrl!}
          title={playingConversion!.title || 'Untitled conversion'}
          subtitle={playingConversion!.sourceUrl}
          onClose={() => { setPlayingConversion(null); setPlayingAudioUrl(null); }}
        />
      )}
    </Layout>
  );
}
